import React, { useEffect, useState } from 'react';
import { Box, Button, Stack } from '@mui/material';

import StepperDots from '../../../StepperDots';

import FirstStep from './FirstStep';
import SecondStep from './SecondStep';
import {
  useCreateTaskTemplateMutation,
  useUpdateTaskTemplateMutation,
} from '../../../../services/tasksSlice/tasksSlice';
import { TaskTemplate } from '../../../../../shared/types/Task';
import { useGetUserQuery } from '../../../../services/usersSlice';

interface AddAdminTaskProps {
  selectedTask: TaskTemplate;
  onClose: () => void;
  templateTasksShortNameList: string[];
  setLoadingData: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddAdminTask = ({ selectedTask, onClose, templateTasksShortNameList, setLoadingData }: AddAdminTaskProps) => {
  const [createTaskTemplate] = useCreateTaskTemplateMutation();
  const [updateTaskTemplate] = useUpdateTaskTemplateMutation();
  const [currentStep, setCurrentStep] = useState(0);
  const [description, setDescription] = useState('');
  const [task, setTask] = useState(selectedTask);
  const [ isAdminShortNameValid, setIsAdminShortNameValid ] = useState<boolean>(false);
  const { data: userADInfo } = useGetUserQuery(selectedTask.defaultAssignee as string  | undefined, {
    skip: selectedTask.defaultAssignee == 'requestor' || selectedTask.defaultAssignee == 'stakeholder' || task.defaultAssignee == 'Other' || selectedTask.defaultAssignee == 'system task' || selectedTask.defaultAssignee == 'By Variable',
  });

  useEffect(() => {
    setDescription(selectedTask.description ?? '');
    setTask(selectedTask);
    setIsAdminShortNameValid(selectedTask.shortName ? true : false);
  }, [selectedTask.id, selectedTask.isMultiDepartment, selectedTask.additionalDepartmentIDs]); // Update when task changes or multi-department fields change


  const handleChange = (
    e: React.ChangeEvent<any> | { target: { name: string; value: any } }
  ) => {
    const target = e.target as { name: string; value: any; checked?: boolean };
    const { name, value } = target;
    const checked = target.checked;
    
    // Special case: updating multiple fields at once
    if (name === 'isMultiDepartment_and_additionalDepartmentIDs' && typeof value === 'object') {
      setTask({ ...task, ...value });
      return;
    }
    
    // Special case for multi-select array values
    if (name === 'additionalDepartmentIDs') {
      setTask({ ...task, additionalDepartmentIDs: value as number[] });
      return;
    }
    
    // Handle boolean values directly
    if (typeof value === 'boolean') {
      setTask({ ...task, [name]: value });
    } else {
      setTask({ ...task, [name]: name === 'review' ? checked : value });
    }
  };

  const handleChangeOfDefaultAssignee = (
    defaultAssignee: string
  ) => {
   
   setTask({ ...task, 'defaultAssignee':defaultAssignee});

  };

  const handleCancel = () => {
    if (currentStep === 0) {
      setDescription('');
      setTask(selectedTask);
      onClose();
    } else {
      setCurrentStep(0);
    }
  };

  // Validation for multi-department: if isMultiDepartment is true, must have at least 1 additional department selected
  // (primary department is already selected in departmentID)
  const multiDepartmentValidation = 
    task.isMultiDepartment === true
      ? (task.additionalDepartmentIDs && task.additionalDepartmentIDs.length > 0)
      : true;

  const buttonValidation =
    currentStep === 0 
      ? !task.name || !task.shortName || !isAdminShortNameValid 
      : !task.departmentID || !multiDepartmentValidation;

  const handleSave = async (type: string) => {
    const { name, shortName, defaultAssignee, departmentID, review, phase, stakeholderSummary, requestorSummary, groupName, groupId, taskAge, requestorSla, stakeholderSla, assigneeVariableId, isMultiDepartment, additionalDepartmentIDs } =
      task;
    const newTask = {
      name,
      shortName,
      description,
      departmentID,
      review,
      phase,
      defaultAssignee: defaultAssignee || null,
      stakeholderSummary,
      requestorSummary,
      groupName,
      groupId : groupId || null,
      taskAge : taskAge || null,
      requestorSla: requestorSla || null,
      stakeholderSla: stakeholderSla || null,
      assigneeVariableId: assigneeVariableId || null,
      isMultiDepartment: isMultiDepartment || false,
      additionalDepartmentIDs: isMultiDepartment ? (additionalDepartmentIDs || []) : []
    };
    if (currentStep === 0) {
      setCurrentStep(1);
    } else {
      
      setCurrentStep(0);
      setTask(selectedTask);
      setDescription('');
      onClose();
      setLoadingData(true);
      try {
        if (type === 'Add') {
          await createTaskTemplate(newTask).unwrap();
        } else {
          await updateTaskTemplate({ id: selectedTask.id, body: newTask }).unwrap();
        } 
      } catch (error) {
        console.error('Error saving task template:', error);
      } finally {
        setLoadingData(false);
      }
    }
  };

  return (
    <Box sx={{ overflow: 'scroll', height: 700}}>
      <>
        {currentStep === 0 ? (
          <FirstStep
            taskid={selectedTask?.id}
            task={task}
            handleChange={handleChange}
            description={description}
            setDescription={setDescription}
            templateTasksShortNameList= {templateTasksShortNameList}
            setIsAdminShortNameValid = {setIsAdminShortNameValid}
          />
        ) : (
          <SecondStep 
          task={task} 
          handleChange={handleChange} 
          handleChangeOfDefaultAssignee={handleChangeOfDefaultAssignee} 
          displayName = {(task?.defaultAssignee && task?.defaultAssignee?.length > 11) ? userADInfo?.displayName : null}/>
        )}
      </>
      <StepperDots steps={2} activeStep={currentStep} />
      <Stack
        direction="row"
        display="flex"
        justifyContent="right"
        spacing={2}
        sx={{
          px: 4,
          pb: 4,
        }}
      >
        <Button
          sx={{
            color: '#2372B9',
            textTransform: 'none',
          }}
          variant="text"
          onClick={handleCancel}
        >
          {currentStep === 0 ? 'Cancel' : 'Back'}
        </Button>
        {/* PASS TASK NAME */}
        <Button
          sx={{
            backgroundColor: '#2372B9',
            textTransform: 'none',
          }}
          variant="contained"
          disabled={buttonValidation}
          onClick={() => handleSave(selectedTask.name === '' ? 'Add' : 'Edit')}
          type="submit"
        >
          {currentStep === 0
            ? 'Next'
            : selectedTask.name === ''
            ? 'Add Task'
            : 'Edit Task'}
        </Button>
      </Stack>
    </Box>
  );
};

export default AddAdminTask;
